import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { products } from "../data/products";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { enterProduct } from "../lib/navigation";
import type { Product } from "../types";
import { BrandLogo } from "./BrandLogo";
import { ProductGlyph } from "./ProductGlyph";
import { StatusPill } from "./StatusPill";

export function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef<HTMLElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useBodyScrollLock(mobileOpen);

  useEffect(() => {
    setMenuOpen(false);
    setMobileOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    function closeOnOutside(event: PointerEvent) {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setMobileOpen(false);
      }
    }
    document.addEventListener("pointerdown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  function handleProduct(product: Product) {
    setMenuOpen(false);
    setMobileOpen(false);
    enterProduct(product);
  }

  const isHome = location.pathname === "/";

  return (
    <header className="site-header" ref={navRef}>
      <button
        className="site-header__logo"
        type="button"
        onClick={() => navigate("/", { replace: false })}
        aria-label="返回首页"
      >
        <BrandLogo compact />
      </button>

      <nav className="desktop-nav" aria-label="主导航">
        <NavLink
          to="/"
          end
          className={({ isActive }) => (isActive ? "is-active" : undefined)}
        >
          首页
        </NavLink>

        <div
          className="desktop-nav__product"
          onMouseEnter={() => setMenuOpen(true)}
          onMouseLeave={() => setMenuOpen(false)}
        >
          <button
            type="button"
            className={menuOpen ? "is-active" : undefined}
            aria-expanded={menuOpen}
            aria-controls="product-menu"
            onClick={() => setMenuOpen((open) => !open)}
            onFocus={() => setMenuOpen(true)}
          >
            产品与解决方案{" "}
            <span className="nav-caret" aria-hidden="true">
              <svg viewBox="0 0 16 16">
                <path d="m4 6 4 4 4-4" />
              </svg>
            </span>
          </button>
          <div
            id="product-menu"
            className={`product-menu ${menuOpen ? "is-open" : ""}`}
          >
            <div className="product-menu__head">
              <span>产品矩阵</span>
            </div>
            <div className="product-menu__grid">
              {products.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  className="product-menu__item"
                  onClick={() => handleProduct(product)}
                >
                  <span className={`menu-mark menu-mark--${product.id}`}>
                    <ProductGlyph type={product.id} theme="light" />
                  </span>
                  <span>
                    <strong>{product.name}</strong>
                    <small>{product.plainName}</small>
                  </span>
                  <StatusPill product={product} />
                </button>
              ))}
            </div>
          </div>
        </div>

        <NavLink
          to="/security"
          className={({ isActive }) => (isActive ? "is-active" : undefined)}
        >
          安全与合规
        </NavLink>
      </nav>

      <button
        className="mobile-menu-button"
        type="button"
        aria-expanded={mobileOpen}
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        <span></span>
        <span></span>
        <span></span>
        <span className="sr-only">打开菜单</span>
      </button>

      <div className={`mobile-nav ${mobileOpen ? "is-open" : ""}`}>
        <button
          type="button"
          className={isHome ? "is-active" : undefined}
          onClick={() => navigate("/")}
        >
          首页
        </button>
        <button type="button" onClick={() => setMenuOpen(!menuOpen)}>
          产品与解决方案 <span>⌄</span>
        </button>
        <div className={`mobile-products ${menuOpen ? "is-open" : ""}`}>
          {products.map((product) => (
            <button
              className="mobile-product-link"
              key={product.id}
              type="button"
              onClick={() => handleProduct(product)}
            >
              <span className={`menu-mark menu-mark--${product.id}`}>
                <ProductGlyph type={product.id} theme="light" />
              </span>
              <span>{product.name}</span>
              <StatusPill product={product} />
            </button>
          ))}
        </div>
        <button type="button" onClick={() => navigate("/security")}>
          安全与合规
        </button>
      </div>
    </header>
  );
}
